import { Store } from 'vuex';

import { AppState } from '@/store';
import { MUTATIONS as customerMutations } from '@/store/customer';
import { LoyaltyService } from '@/services/loyalty/loyalty.service';
import { sleep } from '@/helpers/util';

declare global {
  interface Window {
    lpTag: {
      sdes: {
        push(sde: any): void;
      };
    };
  }
}

export interface PepperChatService {
  openChatWindow(): void;
  endConversation(): Promise<void>;
  sendCustomerInfo(): void;
}

export class AppPepperChatService implements PepperChatService {
  constructor(private store: Store<AppState>, private loyaltyService: LoyaltyService) {
    /**
     * This is the easiest way given the existing code to ensure customer profile changes are synced to LP for Pepper chatbot
     * See "TODO-d8ehf5" in "axios-factory.ts" for the rationale behind having separate mutations for changing the profile.
     */
    store.subscribe(mutation => {
      switch (mutation.type) {
        case customerMutations.RESET_PROFILE:
          this.endConversation();
          break;
        case customerMutations.REFRESH_PROFILE:
          this.sendCustomerInfo();
          break;
        case customerMutations.SET_CUSTOMER_PROFILE:
          mutation.payload === null ? this.endConversation() : this.sendCustomerInfo();
      }
    });
  }

  openChatWindow(): void {
    // If LivePerson is setup correctly there should be a hidden "engagement" element in the DOM which triggers opening Pepper
    document.querySelector<HTMLDivElement>('div[data-LP-event="click"]')?.click();
  }

  async endConversation(): Promise<void> {
    /**
     * HACK: This hack exists since we want the ability to end the conversation and clear chat history when our user signs out.
     * LivePerson allegedly couldn't expose this functionality through their SDK but the UI allows it.  We should research LOE
     * to use LivePerson's websocket based messaging API since it may have this capability.
     *
     * Calling sleep is necessary since we want to ensure:
     * 1. the bot is ready to receive next input
     * 2. we don't reload the page before ending the conversation is confirmed - see "signOut" method in "cmg-account-modal-menu-links.ts"
     */
    document.querySelector<HTMLButtonElement>('button[data-lp-point="close"]')?.click();
    await sleep(50);
    document.querySelector<HTMLButtonElement>('button[data-lp-point="confirm_button"]')?.click();
    await sleep(50);
  }

  sendCustomerInfo(): void {
    const { profile } = this.store.state.customer;
    const lastOrder = this.store.state.order.lastOrder.order?.order;
    const sde = {
      type: 'personal',
      personal: {
        firstname: profile?.firstName ?? null,
        lastname: profile?.lastName ?? null,
        contacts: [
          {
            email: profile?.email ?? null,
            phone: profile?.phoneNumber ?? null,
          },
        ],
        company: JSON.stringify({
          isGuestUser: profile?.isGuest ?? false,
          isLoyaltyMember: this.loyaltyService.isEnrolledInLoyalty(profile),
          channel: 'web',
          orderConfId: lastOrder?.orderShortCode ?? null,
        }),
      },
    };
    
    if(typeof window.lpTag === 'undefined') {
      window.lpTag = { sdes: [] };
    }
    window.lpTag.sdes.push(sde);
  }
}
